import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AnswerCards from '../../components/AnswerCards/AnswerCards';
import RangeContainer from '../../components/RangeContainer/RangeContainer';
import { useGame } from '../../context/GameContext';
import { ButtonLarge } from '../../components/common/buttons/ButtonLarge/ButtonLarge';
import { QuizFooter, QuizFooterRow } from '../../components/common/QuizFooter';
import { STACKS_BY_FORMAT } from '../../screens/BlastConfigScreen/blastRangeRules';

import './blast-answer-screen.css';

// ─── Fixed Hands helpers ──────────────────────────────────────────────────────

const RANK_ORDER = 'AKQJT98765432';

/**
 * Build the canonical hand key ('AA', 'AKs', 'AKo') from selectedHand
 * where selectedHand = { card1: 'Kh', card2: 'Qh' }.
 */
const buildHandKey = ({ card1, card2 }) => {
  const v1 = card1[0], v2 = card2[0], s1 = card1[1], s2 = card2[1];
  const isPair   = v1 === v2;
  const isSuited = !isPair && s1 === s2;
  const hi = RANK_ORDER.indexOf(v1) <= RANK_ORDER.indexOf(v2) ? v1 : v2;
  const lo = hi === v1 ? v2 : v1;
  return isPair ? hi + hi : isSuited ? hi + lo + 's' : hi + lo + 'o';
};

/**
 * Normalise a raw CSV action + frequency into a display label for circles.
 * Position context matters: SB can't check preflop — "check" in SB CSVs means limp.
 * Priority when two actions tie: allin > raise > call/limp > check > fold.
 */
/**
 * isCallContext: true when the hero is genuinely calling a villain's bet (not open-limping).
 * When true, SB's CSV 'call' action is displayed as 'Call', not 'Limp'.
 */
const normalizeCircleAction = (action, frequency, position, isCallContext = false) => {
  if (!frequency || frequency <= 0) return 'Fold';
  const a = (action || '').toLowerCase();
  const isSB = (position || '').toUpperCase() === 'SB';
  if (a === 'allin')                                       return 'Jam';
  if (a === 'raise_large' || a === 'raise-large')          return 'Large Raise';
  if (a === 'raise_small' || a === 'raise-small')          return 'Small Raise';
  if (a === 'raise' || a.startsWith('raise_'))             return 'Raise';
  if (a === 'limp')                                        return 'Limp';
  if (a === 'call')                                        return (isSB && !isCallContext) ? 'Limp' : 'Call';
  if (a === 'check') {
    // Mirrors the 'call' branch above: SB's own opening decision reads 'check'
    // as 'Limp' (SB can't check preflop), but when SB is genuinely reacting to
    // a bet (isCallContext) — e.g. SB limped, BB raised, and the CSV spells
    // that calling response 'check' instead of 'call' (confirmed in
    // sb_vs_sb_call_vs_bb_raise_large/small_*.csv, which have no 'call' rows
    // at all, only 'allin'/'check') — that 'check' means SB is calling the
    // raise, not completing the blind again, and must read as 'Call'.
    if (isSB) return isCallContext ? 'Call' : 'Limp';
    // CSV data uses 'check' interchangeably with 'call' for facing-a-bet scenarios
    // (BB/BTN/etc. can't literally check preflop when facing an open/raise/jam —
    // e.g. bb_vs_btn_open_15bb.csv spells the same call action 'check' while
    // bb_vs_btn_open_12bb.csv spells it 'call'). Treat it exactly like 'call' in
    // those contexts so the answer circles always agree with the oval graphic
    // above, which already reads 'check' as a call via isLimpAct() in
    // getBlastCorrectLabel(). No CSV row ever encodes an explicit 'fold' action —
    // folds are implicit (100% minus the listed actions) — so 'check' never
    // stands in for a real fold and must not be downgraded to one here.
    return isCallContext ? 'Call' : 'Check';
  }
  if (a === 'fold')                                        return 'Fold';
  return 'Fold';
};

const ACTION_PRIORITY = { Jam: 6, 'Large Raise': 5, 'Small Raise': 4, Raise: 4, Call: 3, Limp: 3, Check: 2, Fold: 1 };

const ACTION_COLORS = {
  Jam:          '#ba4a30',
  'Large Raise':'#D44221',
  'Small Raise':'#e5735a',
  Raise:        '#e5735a',
  Call:         '#49ab78',
  Limp:         '#49ab78',
  Check:        '#49ab78',
  Fold:         '#2B6886',
  OOR:          '#494744',
};

const getActionColor = (label) => ACTION_COLORS[label] || '#e5735a';

/**
 * Normalises a hand's full CSV action list (allActions-shaped: [{action,
 * frequency}, ...]) into a sorted "signature" of display-label entries —
 * e.g. [{label:'Large Raise', freq:50}, {label:'Check', freq:50}] — using the
 * same raise-sizing and position-aware rules as everywhere else in this file
 * (parseHandActions, getRangeSvgPath, normalizeCircleAction). Sorted by
 * ACTION_PRIORITY (highest first) so two equivalent action sets always
 * produce index-for-index-comparable arrays regardless of CSV row order.
 */
const buildMergedLabelEntries = (allActions, raiseSizes, heroPos, isCallCtx) => {
  const hasMultipleRaises = (raiseSizes || []).length >= 2;
  const largestRaiseSize  = raiseSizes?.[0];
  const tally = {};
  for (const { action, frequency } of (allActions || [])) {
    if (!frequency || frequency <= 0) continue;
    const a = (action || '').toLowerCase();
    let label;
    if (a === 'raise' || a.startsWith('raise_')) {
      if (hasMultipleRaises) {
        const size = a.startsWith('raise_') ? parseFloat(a.slice(6)) : 0;
        label = size >= largestRaiseSize ? 'Large Raise' : 'Small Raise';
      } else {
        label = 'Raise';
      }
    } else {
      label = normalizeCircleAction(a, frequency, heroPos, isCallCtx);
    }
    tally[label] = (tally[label] || 0) + frequency;
  }
  return Object.entries(tally)
    .map(([label, freq]) => ({ label, freq }))
    .sort((x, y) => (ACTION_PRIORITY[y.label] || 0) - (ACTION_PRIORITY[x.label] || 0));
};

/**
 * True when two buildMergedLabelEntries() outputs represent the exact same
 * action mix — same actions, same frequencies, in the same (priority-sorted)
 * order. Used to find "other hands" that match a merged/mixed-range answer
 * exactly, rather than just sharing one action bucket.
 */
const sameActionSignature = (a, b) =>
  a.length === b.length && a.every((e, i) => e.label === b[i].label && e.freq === b[i].freq);

/**
 * Formats a merged-range signature (from buildMergedLabelEntries) for display.
 * A single real action (paired only with an implicit, unnamed Fold remainder)
 * keeps its own percentage — e.g. "Large Raise 50%" — since there's no second
 * named action for the split graphic to represent. Two or more real actions
 * (a genuine merged/mixed range, e.g. Large Raise 50% / Check 50%) combine
 * into one slash-joined label with NO percentage — the oval's two-color split
 * background already conveys the split, so repeating "50%" next to it is
 * redundant. Shares a trailing word (e.g. "Large Raise" + "Small Raise") the
 * same way the Fixed Hands circle labels already do, e.g. "Large/Small Raise".
 */
const formatMergedActionLabel = (entries) => {
  if (!entries.length) return 'Fold';
  if (entries.length === 1) {
    const { label, freq } = entries[0];
    return freq < 100 ? `${label} ${freq}%` : label;
  }
  const [first, second] = entries;
  const wordsA = first.label.split(' ');
  const wordsB = second.label.split(' ');
  const lastA = wordsA[wordsA.length - 1];
  const lastB = wordsB[wordsB.length - 1];
  if (lastA === lastB && wordsA.length > 1 && wordsB.length > 1) {
    return `${wordsA.slice(0, -1).join(' ')}/${wordsB.slice(0, -1).join(' ')} ${lastA}`;
  }
  return `${first.label}/${second.label}`;
};

/** CSS modifier class from a normalized action label */
const circleClass = (label) => {
  switch (label) {
    case 'Jam':          return 'fh-circle--jam';
    case 'Large Raise':  return 'fh-circle--raise-large';
    case 'Small Raise':  return 'fh-circle--raise-small';
    case 'Raise':        return 'fh-circle--raise';
    case 'Limp':         return 'fh-circle--limp';
    case 'Call':         return 'fh-circle--call';
    case 'Check':        return 'fh-circle--check';
    case 'Never':        return 'fh-circle--never';
    case 'Fold':         return 'fh-circle--fold';
    case 'OOR':          return 'fh-circle--oor';
    default:             return 'fh-circle--fold';
  }
};

/**
 * Maps a sub-range reaction quiz's own primaryAction to the filterMode used
 * against hero's own OPENING-range CSV (not the reaction CSV) to check
 * whether the tested hand ever takes this line at all at a given stack.
 *
 * Reaction quizzes (e.g. "BTN Raise-call vs BB 3Bet Jam") test hero's
 * response AFTER hero's own open_raise/open_limp — but that opening action
 * has its own range. At some stacks a hand isn't in it (e.g. AKo is a
 * 100%-jam hand for BTN at 12bb, never a raise), so the reaction CSV
 * legitimately has no row for it. Only open_raise/open_limp gate hero's own
 * hand like this — open_jam is already an all-in, terminal decision with
 * nothing further to be "out of range" for.
 */
const ANTECEDENT_FILTER_BY_PRIMARY = { open_raise: 'raise', open_limp: 'call' };

/**
 * True when `handKey` has real (non-fold) frequency for the antecedent action
 * (raise or limp) in hero's own opening-range CSV at this stack. False means
 * the hand never takes this line here — the reaction CSV's "no row found"
 * means "out of range", not "folds", and callers should show OOR instead of
 * falling through to parseHandActions' Fold default.
 *
 * Fails open (returns true, i.e. "don't block") on fetch/parse failure so a
 * missing/unreachable opening CSV degrades to the old Fold behavior rather
 * than mislabeling every stack OOR.
 */
const isHandInOpeningRange = async (format, position, stack, handKey, filterMode) => {
  try {
    const formatPath = format === '3way' ? '3_way' : 'hu';
    const posKey = (position || '').toLowerCase();
    const path = `/database/blast/preflop/${formatPath}/${posKey}_open/${posKey}_open_${stack}bb.csv`;
    const res = await fetch(path);
    if (!res.ok) return true;
    const text = await res.text();
    const entries = parseHandActions(text, handKey, position, 'open', filterMode);
    const realFreq = entries.filter(e => e.label !== 'Fold').reduce((sum, e) => sum + e.freq, 0);
    return realFreq > 0;
  } catch {
    return true;
  }
};

/**
 * True when `handKey` appears as an explicit, non-zero-frequency row anywhere
 * in a reaction CSV, regardless of which action it plays there. Solver CSVs
 * never write an explicit 'fold' row — a hand's total absence from the file
 * is how a 100% fold is spelled — so finding any row here means this stack's
 * solve actually defines an answer for the hand.
 *
 * Used to gate isHandInOpeningRange: that antecedent check exists only to
 * label hands OOR when the reaction CSV is silent on them (no way to tell
 * "genuinely folds this line" from "never reaches this line" otherwise). When
 * the reaction CSV *does* have a real row for the hand — e.g. A5s/A5o both
 * listed as 100% call in sb_vs_sb_open_bb_ai_15bb.csv even though A5s is a
 * 100%-limp (never-raise) hand in sb_open_15bb.csv at that same stack — that
 * row is a genuine solved answer and must be trusted over the antecedent
 * guess, not discarded in favor of a manufactured OOR.
 */
const handHasAnyRow = (csvText, handKey) => {
  const lines = csvText.trim().split('\n');
  if (lines.length < 2) return false;
  const headers = lines[0].toLowerCase().split(',').map(h => h.trim());
  const handIdx = headers.indexOf('hand');
  const typeIdx = headers.indexOf('type');
  const freqIdx = headers.indexOf('frequency');
  if (handIdx === -1) return false;

  for (let i = 1; i < lines.length; i++) {
    const parts = lines[i].split(',').map(v => v.trim());
    if (parts.length < 2) continue;

    const rowHand = parts[handIdx] || '';
    const rowType = typeIdx !== -1 ? parts[typeIdx] : '';
    const freqRaw = freqIdx !== -1 ? parts[freqIdx] : '';

    const base   = rowHand.replace(/[so]$/, '');
    const suffix = rowHand.endsWith('s') ? 's'
                 : rowHand.endsWith('o') ? 'o'
                 : (base.length === 2 && base[0] === base[1]) ? ''
                 : rowType === 'suited' ? 's' : 'o';
    const csvKey = base + suffix;
    if (csvKey !== handKey) continue;

    const freq = parseInt(String(freqRaw).replace('%', ''));
    if (!isNaN(freq) && freq > 0) return true;
  }
  return false;
};

/**
 * Path-specific scenario notes — keyed by `${format}|${position}|${primaryAction}|${secondaryAction}`
 * (tertiaryAction is ignored: jam/call/mixed response filters don't change which
 * stack depths a path's underlying strategy applies to).
 *
 * These replace the old one-size-fits-all disclaimer with the actual strategy
 * shift that explains why some stack depths don't show every action — reviewed
 * and confirmed per-path (2026-07-07). Any path not listed here falls back to
 * the generic ladder-gap message in getScenarioNote below.
 */
const SCENARIO_NOTES = {
  // ── 3-Way BTN ──────────────────────────────────────────────────────────────
  '3way|BTN|open_raise|':        'Below 12bb, BTN only jams.',
  '3way|BTN|open_mixed|':        'Below 12bb, BTN only jams.',
  '3way|BTN|open_raise|sb_jam':  'Below 12bb, BTN only jams.',  // BTN Raise response types — same raise-ladder gap
  '3way|BTN|open_raise|bb_jam':  'Below 12bb, BTN only jams.',
  '3way|BTN|open_raise|bb_3bet': 'Below 15bb, BB only jams as a 3bet.',

  // ── 3-Way SB ───────────────────────────────────────────────────────────────
  '3way|SB|open_raise|': 'Below 12bb, SB either jams or limps.',
  '3way|SB|open_limp|':  'Below 7bb, SB only open jams.',
  '3way|SB|open_mixed|': 'Raise disappears below 12bb. Limp disappears below 7bb.',
  // SB Limp response types (BB Jam / BB Large Raise / BB Small Raise) — same
  // limp-ladder gap as open_limp above, since SB never limped there to begin with.
  '3way|SB|open_limp|sb_limp_bb_jam':         'Below 7bb, SB only open jams.',
  '3way|SB|open_limp|sb_limp_bb_raise_large': 'Below 7bb, SB only open jams.',
  '3way|SB|open_limp|sb_limp_bb_raise_small': 'Below 7bb, SB only open jams.',

  // SB jams, BTN raises back, SB responds — this is BTN's opening range showing
  // up again (SB jamming implies BTN opened first), so it gets the BTN Open
  // note. Matches the data exactly: only wired at 15bb/12bb, same as BTN's own
  // raise ladder.
  '3way|SB|open_jam|sb_btn_raise': 'Below 12bb, BTN only jams.',

  // ── 3-Way BB ───────────────────────────────────────────────────────────────
  // Rule: when BB is reacting to another position's opening action, the note
  // belongs to whichever position opened — their opening range is what shifts
  // with stack depth, regardless of what BB's own response options are called.
  '3way|BB|bb_vs_btn_open|':     'Below 12bb, BTN only jams.',        // BTN opened
  '3way|BB|bb_vs_btn_open|jam':  'Below 12bb, BTN only jams.',
  '3way|BB|bb_vs_btn_open|call': 'Below 12bb, BTN only jams.',
  '3way|BB|bb_vs_btn_open|mixed':'Below 12bb, BTN only jams.',

  '3way|BB|bb_vs_sb_open|':      'Below 12bb, SB either jams or limps.',  // SB's own open-raise
  '3way|BB|bb_vs_sb_open|jam':   'Below 12bb, SB either jams or limps.',
  '3way|BB|bb_vs_sb_open|call':  'Below 12bb, SB either jams or limps.',
  '3way|BB|bb_vs_sb_open|mixed': 'Below 12bb, SB either jams or limps.',

  '3way|BB|bb_vs_sb_call|':            'Below 7bb, SB only open jams.',  // SB's own open-limp
  '3way|BB|bb_vs_sb_call|jam':         'Below 7bb, SB only open jams.',
  '3way|BB|bb_vs_sb_call|raise_large': 'Below 7bb, SB only open jams.',
  '3way|BB|bb_vs_sb_call|raise_small': 'Below 7bb, SB only open jams.',
  '3way|BB|bb_vs_sb_call|check':       'Below 7bb, SB only open jams.',
  '3way|BB|bb_vs_sb_call|mixed':       'Below 7bb, SB only open jams.',

  '3way|BB|bb_vs_btn_open_vs_sb_ai|': 'Below 12bb, BTN only jams.',  // BTN opened first

  // '3way|BB|bb_vs_sb_ai' (BB facing SB Jam) intentionally has no entry — it
  // was a rules-config gap, not a real strategy cutoff (3way_BB_4 was missing
  // the 'bb_vs_sb_ai' primaryAction even though bb_vs_sb_ai_4bb.csv exists).
  // Fixed directly in blastRangeRules.js's 3way_BB_4 entry — this path now
  // covers every stack, so no note is needed here at all.

  // ── Heads Up BB ────────────────────────────────────────────────────────────
  'hu|BB|bb_vs_sb_ai|':   'At 25bb, SB has no jams.',
  'hu|BB|bb_vs_sb_call|':             'Below 6bb, SB only jams.',  // SB's own open-limp
  'hu|BB|bb_vs_sb_call|jam':          'Below 6bb, SB only jams.',
  'hu|BB|bb_vs_sb_call|raise_large':  'Below 6bb, SB only jams.',
  'hu|BB|bb_vs_sb_call|raise_small':  'Below 6bb, SB only jams.',
  'hu|BB|bb_vs_sb_call|check':        'Below 6bb, SB only jams.',
  'hu|BB|bb_vs_sb_call|mixed':        'Below 6bb, SB only jams.',

  // SB's own open-raise disappears below 15bb in HU (not 12bb — HU's ladder has
  // no 12bb stack; the real cutoff, per blastRangeRules.js, is between 15bb and
  // 10bb). Applies to every BB response filter, since SB opened.
  'hu|BB|bb_vs_sb_open|':      'Below 15bb, SB either jams or limps.',
  'hu|BB|bb_vs_sb_open|jam':   'Below 15bb, SB either jams or limps.',
  'hu|BB|bb_vs_sb_open|call':  'Below 15bb, SB either jams or limps.',
  'hu|BB|bb_vs_sb_open|mixed': 'Below 15bb, SB either jams or limps.',

  // BB's own re-raise sizing collapses below 25bb — at 15bb BB can only jam or
  // call over SB's raise, no separate large/small re-raise choice.
  'hu|BB|bb_vs_sb_open|raise_large': 'At 15bb, BB will only jam or call.',
  'hu|BB|bb_vs_sb_open|raise_small': 'At 15bb, BB will only jam or call.',

  // ── Heads Up SB ────────────────────────────────────────────────────────────
  'hu|SB|open_raise|sb_vs_sb_raise_vs_bb_3b_large': 'At 15bb, BB switches to a 3bet small strategy.',
  'hu|SB|open_raise|sb_vs_sb_raise_vs_bb_3b_small': 'Below 12bb, SB only calls.',

  // SB's own opening actions — no reaction chain, straightforward ladder gaps.
  'hu|SB|open_raise|':                   'Below 15bb, SB either jams or limps.',
  'hu|SB|open_raise|sb_vs_sb_open_bb_ai':'Below 15bb, SB either jams or limps.',

  'hu|SB|open_limp|':                              'Below 6bb, SB only jams.',
  'hu|SB|open_limp|sb_vs_sb_limp_vs_bb_jam':       'Below 6bb, SB only jams.',
  'hu|SB|open_limp|sb_vs_sb_call_vs_bb_raise_large':'Below 6bb, SB only jams.',
  'hu|SB|open_limp|sb_vs_sb_call_vs_bb_raise_small':'Below 6bb, SB only jams.',

  'hu|SB|open_jam|': 'At 25bb, SB has no jam.',
};

/**
 * Returns a short gray disclaimer note explaining why the selected path's
 * circles don't cover every stack depth in the format+position's full ladder
 * (STACKS_BY_FORMAT). Only returns a note at all when availableStacks is
 * actually missing one or more depths; when every depth is shown, returns
 * null regardless of whether SCENARIO_NOTES has copy for this path. Once a
 * gap is confirmed, checks SCENARIO_NOTES for a reviewed, path-specific
 * explanation first; falls back to a generic message for any gapped path not
 * yet reviewed (no specific copy written for it — see SCENARIO_NOTES above).
 */
const getScenarioNote = (config) => {
  if (!config) return null;
  const format = config.format;
  const position = (config.position || config.heroPosition || '').toUpperCase();
  const primaryAction = config.primaryAction ?? '';
  const secondaryAction = config.secondaryAction ?? '';

  // Coverage check runs first: the note only makes sense when this path's
  // circles are actually missing one or more stack depths from the full
  // ladder. Checking this before the SCENARIO_NOTES lookup (rather than
  // after) means a path with a reviewed, specific explanation still won't
  // show it once every stack depth happens to be available — e.g. 3-way BTN
  // Open Raise at 15/12/9/6/4bb shows every stack in STACKS_BY_FORMAT, so
  // "Below 12bb, BTN only jams." would be a confusing non-sequitur there even
  // though the note is correct for paths that truly skip a depth.
  const allStacks = STACKS_BY_FORMAT[format]?.[position];
  const availableStacks = config.availableStacks;

  if (!allStacks?.length || !availableStacks?.length) return null;
  if (availableStacks.length >= allStacks.length) return null;

  const specific = SCENARIO_NOTES[`${format}|${position}|${primaryAction}|${secondaryAction}`];
  if (specific) return specific;

  return 'Only stack depths where this action is available are shown.';
};

/**
 * Returns { formatLine, actionLine } for the two-line scenario label.
 * `includeCompleteRange` appends "| Complete Range" to the format line —
 * used on the Mixed Blinds/Fixed Hands (circles) screen only; the Fixed
 * Blinds (range grid) screen shows the plain format line instead.
 */
const getScenarioLines = (config, includeCompleteRange = false) => {
  let fmt = config.format === 'hu' ? 'Heads Up' : '3 Way';
  if (includeCompleteRange) fmt += ' | Complete Range';
  const pos = config.heroPosition || config.position || '';
  const { action, subtype, villainAction, villainPos } = config;
  const va = (villainAction || '').toLowerCase();

  let actionLabel = '';
  // BB facing SB limp — label depends on subtype (raise-large, raise-small, jam, or generic option)
  if (config.primaryAction === 'bb_vs_sb_call' || config.primaryAction === 'bb_vs_sb_call_vs_btn_open') {
    const st = (subtype || '').toLowerCase();
    if (st === 'raise-large' || st === 'raise_large')       actionLabel = 'Large Raise';
    else if (st === 'raise-small' || st === 'raise_small')  actionLabel = 'Small Raise';
    else if (st === 'raise')                                 actionLabel = 'Raise';
    else if (st === 'jam' || st === 'allin')                 actionLabel = 'Jam';
    else                                                     actionLabel = 'Option';
  } else if (action === 'open') {
    // config.subtype is overwritten by filterMode in buildBlastConfig
    // (subtype: filterMode ?? subtype), so a Jam-only open quiz arrives here
    // as 'allin', not 'jam' — without this, it fell through to the generic
    // 'Open' label instead of 'Open Jam'.
    if (subtype === 'jam' || subtype === 'allin')  actionLabel = 'Open Jam';
    else if (subtype === 'raise')                 actionLabel = 'Open Raise';
    else if (subtype === 'call' || subtype === 'limp')
      actionLabel = pos === 'SB' ? 'Limp' : 'Open Call';
    else actionLabel = 'Open';
  } else if (action === 'jam') {
    if (va && villainPos) {
      const vaLabel = va === 'open' ? 'Open' : va.charAt(0).toUpperCase() + va.slice(1);
      actionLabel = `Jam vs ${villainPos} ${vaLabel}`;
    } else {
      actionLabel = 'Jam';
    }
  } else if (action === 'check')    actionLabel = 'Check';
  else if (action === 'raise')      actionLabel = 'Raise';
  else if (action === 'complete')   actionLabel = 'Complete';
  else if (action === 'call') {
    // Clarify hero's own prior action (raise vs limp) so "Call vs BB 3-Bet"
    // isn't ambiguous about whether hero opened with a raise or a limp before
    // facing villain's reaction. A true 3-bet requires hero to have raised
    // first — if hero limped, villain's raise over the limp isn't a "3-bet".
    const isOpenRaise = config.primaryAction === 'open_raise';
    const isOpenLimp  = config.primaryAction === 'open_limp';
    const heroVerb = isOpenRaise ? 'Raise-call' : isOpenLimp ? 'Limp-call' : 'Call';
    if (va === 'jam') {
      // If SB raised first and BB jammed over it, that's a 3Bet Jam — distinct from a cold jam
      actionLabel = isOpenRaise
        ? `${heroVerb} vs ${villainPos || ''} 3Bet Jam`.trim()
        : `${heroVerb} vs ${villainPos || ''} Jam`.trim();
    }
    else if (va === 'limp')                       actionLabel = `vs ${villainPos || ''} Limp`.trim();
    else if (va === 'open')                       actionLabel = `${heroVerb} vs ${villainPos || ''} Open`.trim();
    else if (va === '3bet small' || va === '3bet large')
      actionLabel = `${heroVerb} vs ${villainPos || ''} 3-Bet`.trim();
    else actionLabel = heroVerb;
  } else {
    actionLabel = action ? action.charAt(0).toUpperCase() + action.slice(1) : '';
  }

  return { formatLine: fmt, actionLine: `${pos} ${actionLabel}`.trim() };
};

/**
 * Parse a CSV text and return the top significant actions for the given hand key.
 *
 * Raise sizing is determined by scanning the whole CSV first:
 *   - If 2+ distinct raise sizes exist → largest = 'Large Raise', rest = 'Small Raise'
 *   - If only 1 raise size → 'Raise'
 *
 * Returns an array of { label, freq } sorted by frequency desc.
 * Secondary action is included when freq >= 25% (for split circles).
 * Returns [{ label: 'Fold', freq: 0 }] if the hand is not found.
 *
 * showRealActionWhenTestedIsZero (default false — existing callers are
 * unaffected): when filterMode is set and the tested action's own frequency
 * is genuinely 0% for this hand/stack, the default behavior (false) still
 * collapses that to a flat [{ label: 'Fold', freq: 0 }], exactly as before —
 * isHandInOpeningRange relies on this to detect "hand never takes this line"
 * for its OOR (out-of-range) check, so it must not change. Pass true only
 * where the hand's REAL untested action should be shown instead of a
 * misleading "Fold" (currently: the Fixed Hands circle loader), which falls
 * back to the unfiltered tally in that 0% case, e.g. returning 'Raise'
 * instead of 'Fold' for a hand that's really Raise 100% on a Jam-only quiz.
 *
 * foldableContext (default true — existing callers are unaffected): whether
 * Fold is even a legal action in this scenario. False only for BB facing
 * SB's limp (bb_vs_sb_call/bb_vs_sb_call_vs_btn_open) — SB has merely
 * completed to 1bb with no bet out there, so BB can only check or raise, never
 * fold. When false, the "implicit remainder" step below (2.5) must not label
 * any leftover frequency 'Fold' — it pulls the real complementary action(s)
 * (Check, the other raise size, or Jam) straight from the hand's true,
 * unfiltered tally instead. E.g. a Large-Raise-only sub-range quiz on KQo
 * (really Large Raise 50% / Small Raise 50%, no check at all in
 * bb_vs_sb_call_25bb.csv) must show "Large Raise/Small Raise", not
 * "Large Raise/Fold" — BB never folds this spot, so the raw 25bb button
 * "Small Raise/Fold" was wrong; it should read "Small Raise/Check" (or
 * whatever the hand's real other action is).
 */
const parseHandActions = (csvText, handKey, position, configAction = '', filterMode = '', showRealActionWhenTestedIsZero = false, foldableContext = true) => {
  const lines = csvText.trim().split('\n');
  if (lines.length < 2) return [{ label: 'Fold', freq: 0 }];

  const headers = lines[0].toLowerCase().split(',').map(h => h.trim());
  const handIdx = headers.indexOf('hand');
  const typeIdx = headers.indexOf('type');
  const freqIdx = headers.indexOf('frequency');
  const actIdx  = headers.indexOf('action');

  if (handIdx === -1) return [{ label: 'Fold', freq: 0 }];

  // ── Step 1: scan whole CSV to find all distinct raise actions ──────────────
  const allRaiseActions = new Set();
  if (actIdx !== -1) {
    for (let i = 1; i < lines.length; i++) {
      const parts = lines[i].split(',').map(v => v.trim());
      if (parts.length <= actIdx) continue;
      const a = (parts[actIdx] || '').toLowerCase();
      if (a === 'raise' || a.startsWith('raise_')) allRaiseActions.add(a);
    }
  }

  // Sort raise actions by numeric size descending — largest is 'Large Raise'
  const raiseSizesSorted = [...allRaiseActions]
    .map(a => {
      const num = a.startsWith('raise_') ? (parseFloat(a.slice(6)) || 0) : 0;
      return { action: a, size: num };
    })
    .sort((a, b) => b.size - a.size);

  const hasMultipleSizes  = raiseSizesSorted.length >= 2;
  const largestRaiseKey   = raiseSizesSorted[0]?.action ?? null;

  const raiseLabel = (rawAction) => {
    if (hasMultipleSizes) {
      return rawAction === largestRaiseKey ? 'Large Raise' : 'Small Raise';
    }
    // Only one raise size exists at this stack (e.g. 15bb/12bb have a single
    // solved raise size where 7bb/9bb/10bb have two) — fall back to the quiz's
    // configured subtype so this circle's label/color stays consistent with the
    // oval graphic above and with other stacks' circles, instead of always
    // collapsing to a generic 'Raise' that doesn't match "Large Raise 100%".
    if (filterMode === 'raise-large') return 'Large Raise';
    if (filterMode === 'raise-small') return 'Small Raise';
    return 'Raise';
  };

  // ── Step 2: tally actions for the specific hand key ────────────────────────
  // filteredTally respects filterMode exactly as before. trueTally ignores it —
  // the hand's real, full action mix — and is only ever consulted when
  // showRealActionWhenTestedIsZero is true AND filteredTally comes back empty
  // (see below); every existing caller/behavior is unaffected by its presence.
  const filteredTally = {};
  const trueTally = {};
  for (let i = 1; i < lines.length; i++) {
    const parts = lines[i].split(',').map(v => v.trim());
    if (parts.length < 2) continue;

    const rowHand = parts[handIdx] || '';
    const rowType = typeIdx !== -1 ? parts[typeIdx] : '';
    const freqRaw = freqIdx !== -1 ? parts[freqIdx] : '';
    const rowAct  = actIdx  !== -1 ? parts[actIdx]  : '';

    const base   = rowHand.replace(/[so]$/, '');
    const suffix = rowHand.endsWith('s') ? 's'
                 : rowHand.endsWith('o') ? 'o'
                 : (base.length === 2 && base[0] === base[1]) ? ''
                 : rowType === 'suited' ? 's' : 'o';
    const csvKey = base + suffix;

    if (csvKey !== handKey) continue;

    const freq = parseInt(String(freqRaw).replace('%', ''));
    if (isNaN(freq) || freq <= 0) continue;

    const a = (rowAct || '').toLowerCase();

    // isCallContext: hero is facing a bet (call/jam quiz) — CSV 'check' rows are
    // that scenario's spelling of 'call', not a real check option (see
    // normalizeCircleAction above for the full rationale).
    const isCallCtx = configAction === 'call' || configAction === 'jam';
    const label = (a === 'raise' || a.startsWith('raise_'))
      ? raiseLabel(a)
      : normalizeCircleAction(a, freq, position, isCallCtx);

    // Always tally the hand's real, unfiltered mix.
    trueTally[label] = (trueTally[label] || 0) + freq;

    // When filterMode is set, only tally into filteredTally when the row matches
    // the quiz's target action type. This prevents e.g. "Call/Check" circles
    // appearing on a Jam-frequency quiz.
    let passesFilter = true;
    if (filterMode === 'allin' && a !== 'allin') passesFilter = false;
    // 'check' = SB limp in solver CSVs — treat it the same as call/limp when filtering
    if ((filterMode === 'call' || filterMode === 'limp') && !(a === 'call' || a === 'limp' || a === 'check')) passesFilter = false;
    // Any raise-targeting filterMode ('raise', 'raise-all', 'raise-large', 'raise-small')
    // should only tally raise actions — previously only the bare 'raise' value matched,
    // so 'raise-large'/'raise-small' quizzes (BB Option Type) let check/allin rows leak
    // into the tally instead of being filtered out.
    if (filterMode.startsWith('raise') && a !== 'raise' && !a.startsWith('raise_')) passesFilter = false;
    // 'raise-large'/'raise-small' target one specific raise SIZE, not "any raise" —
    // the check above only confirms the row IS a raise, so without this a hand's
    // Small Raise row still passed a "Large Raise only" filter (both are raises).
    // That kept filteredTally non-empty, which blocked the showRealActionWhenTestedIsZero
    // fallback below from ever kicking in, so a hand that's really Small Raise
    // 50%/Check 50% (0% Large Raise here) rendered as "Large Raise"... falling
    // through to the remainder step as a false "Large Raise/Fold" instead of
    // showing its real Small Raise/Check mix. Reproduced by 92s at HU BB 25bb
    // (bb_vs_sb_call, Large Raise sub-range): real data is Small Raise 50%/Check
    // 50%, but the Large-Raise-filtered circle showed "Small Raise/Fold" — BB
    // facing SB's limp can never fold, so that combination was never valid to
    // begin with (see foldableContext above).
    if (filterMode === 'raise-large' && label !== 'Large Raise') passesFilter = false;
    if (filterMode === 'raise-small' && label !== 'Small Raise') passesFilter = false;
    if (filterMode === 'check' && a !== 'check') passesFilter = false;

    if (passesFilter) {
      filteredTally[label] = (filteredTally[label] || 0) + freq;
    }
  }

  // filteredTally === trueTally whenever filterMode is '' (Mixed mode, nothing
  // filtered out), so using filteredTally below is a no-op change for that case.
  // Only fall back to the unfiltered mix when a filter is active, it excluded
  // every row for this hand (tested action truly 0%), and the caller opted in.
  const tally = (filterMode && Object.keys(filteredTally).length === 0 && showRealActionWhenTestedIsZero)
    ? trueTally
    : filteredTally;

  if (!Object.keys(tally).length) return [{ label: foldableContext ? 'Fold' : 'Check', freq: 0 }];

  // ── Step 2.5: account for the implicit remainder ────────────────────────────
  // CSVs never include an explicit 'fold' row — folds are whatever's left after
  // the listed actions. A hand with a single 'allin,50' row (no second row) is
  // a genuine 50% Jam / 50% Fold mix, not a 100% Jam hand — without this, it
  // rendered as a solid Jam circle while the oval graphic above correctly said
  // "Jam 50%", the same top/bottom mismatch this screen keeps getting reported
  // for. Applies whether or not filterMode is set: for a filtered (single
  // sub-range action) tally, anything that isn't the tested action — whether
  // that's a genuine fold or the hand's real strategy playing some other
  // action entirely — reads as "fold" from that sub-range's point of view,
  // e.g. a Jam-only quiz on a hand that's really Jam 50%/Raise 50% must show
  // "Jam 50%" paired with a 50% Fold circle, not silently drop the other 50%.
  //
  // That "other real action reads as Fold" convention only holds when Fold is
  // actually a legal action here (foldableContext, the default). BB facing
  // SB's limp (bb_vs_sb_call) has no bet to fold to — the remainder is always
  // some other genuinely real action (Check, the hand's other raise size, or
  // Jam), pulled straight from trueTally rather than mislabeled Fold. See
  // foldableContext doc comment above for the KQo example this fixes.
  const usedFreq = Object.values(tally).reduce((sum, f) => sum + f, 0);
  const remainder = 100 - usedFreq;
  if (remainder > 0) {
    if (foldableContext) {
      tally['Fold'] = (tally['Fold'] || 0) + remainder;
    } else {
      let remaining = remainder;
      const trueEntriesSorted = Object.entries(trueTally)
        .filter(([label]) => !(label in tally))
        .sort((a, b) => (ACTION_PRIORITY[b[0]] || 0) - (ACTION_PRIORITY[a[0]] || 0));
      for (const [label, freq] of trueEntriesSorted) {
        if (remaining <= 0) break;
        tally[label] = (tally[label] || 0) + freq;
        remaining -= freq;
      }
      // Shouldn't happen for real bb_vs_sb_call data (every hand's rows sum to
      // 100 — BB always either raises or checks, never folds), but fall back
      // to Check rather than Fold if some leftover is still unaccounted for.
      if (remaining > 0) tally['Check'] = (tally['Check'] || 0) + remaining;
    }
  }

  // ── Step 3: return top significant actions ─────────────────────────────────
  const sorted = Object.entries(tally)
    .map(([label, freq]) => ({ label, freq }))
    .sort((a, b) => b.freq - a.freq || (ACTION_PRIORITY[b.label] || 0) - (ACTION_PRIORITY[a.label] || 0));

  const primary   = sorted[0];
  const secondary = sorted[1];

  // Fold is no longer a mislabeling artifact (normalizeCircleAction never maps a
  // real action to 'Fold' when frequency > 0), so a Fold secondary here is always
  // a genuine partial-fold mix and should be shown as a split like any other.
  if (secondary && secondary.freq >= 25) {
    return [primary, secondary];
  }
  return [primary];
};

/**
 * Build circle groups: consecutive stacks with the same action combination are merged.
 * Returns [{ actions: [{label, freq}], stacks: [25, 15] }, …]
 *
 * `actions` reflects whichever tally parseHandActions was asked to build for this
 * stack. For "Mixed" quizzes (studying the whole Jam/Raise/Limp/Fold decision at
 * once) that's the hand's TRUE, full action mix straight from the CSV, unfiltered.
 * For a single-action sub-range quiz (Jam only, Raise only, etc.) it's filtered
 * down to just that one action vs. Fold (see getSubRangeFilterMode) — a hand that's
 * really Jam 50%/Raise 50% shows as "Jam 50%/Fold 50%" on a Jam-only quiz, because
 * the raise half isn't what's being tested here and shouldn't imply the circles
 * cover a decision the quiz never asks about.
 *
 * Either way, a stack where the tallied result is a genuine 50/50 mix must render
 * as a split (half-and-half) circle, not get silently treated as "100%" of the
 * primary action just because that's what the quiz is studying. Filtering that
 * secondary portion out entirely was what previously caused two visually-identical
 * "Jam" circles to appear side by side (e.g. 15bb at 50% Jam and 12–3bb at 100%
 * Jam) with no way to tell them apart — the % was real, it was just being stripped
 * before it ever reached the circle.
 *
 * The merge key includes each action's frequency, not just its label — otherwise a
 * stack where the hand is a mixed 50% Jam would silently merge with a neighboring
 * stack where the same hand is a pure 100% Jam, and the resulting circle would
 * misrepresent the 50% stack as "always Jam" (see CLAUDE.md bug: BB Jam 25bb
 * showing "Jam 25-6bb" when 25bb is actually only a 50% jam). Circles don't print
 * the percentage as text — the split-color fill (or solid fill for 100%) is what
 * conveys it, so keeping frequency in the merge key is what makes that visual
 * distinction show up as separate circles in the first place.
 */
const buildCircleGroups = (stackResults, currentStack = null) => {
  const groups = [];
  const keyFor = (actions) => actions.map(a => `${a.label}:${a.freq}`).sort().join('+');

  for (const { stack, actions } of stackResults) {
    const key = keyFor(actions);
    const last = groups[groups.length - 1];
    // Merge consecutive groups with the same key
    if (last && last.key === key) {
      last.stacks.push(stack);
    } else {
      groups.push({ key, actions, stacks: [stack] });
    }
  }
  return groups;
};

/**
 * For a single-action sub-range quiz (Jam only, Large Raise only, Call/Limp
 * only, etc.) — i.e. whenever subRangeFreqMode is true — this returns the
 * parseHandActions filterMode that isolates just the tested action, so the
 * Fixed Hands circles agree with the oval graphic/label above them (both are
 * scoped to the one action being studied, not the hand's whole strategy).
 * Mirrors the same config.action/subtype branches subRangeFreqMode and
 * getBlastCorrectLabel already use to identify which action is under test.
 * Returns '' for "Mixed" (and any config subRangeFreqMode doesn't recognize),
 * which tells parseHandActions to tally the hand's true, unfiltered mix.
 */
const getSubRangeFilterMode = (config) => {
  const configAction = config?.action;
  const configSubtype = (config?.subtype || config?.filterMode || '').toLowerCase();

  if (configAction === 'jam' || configSubtype === 'jam' || configSubtype === 'allin') return 'allin';
  if (configAction === 'check') return 'check';
  if (configAction === 'call' || (configAction === 'open' && (configSubtype === 'call' || configSubtype === 'limp'))) return 'call';
  if (configSubtype === 'raise-large' || configSubtype === 'raise_large') return 'raise-large';
  if (configSubtype === 'raise-small' || configSubtype === 'raise_small') return 'raise-small';
  if (configAction === 'raise' || (configAction === 'open' && configSubtype === 'raise')) return 'raise';
  return '';
};

const CARD_VALUES = ['2', '3', '4', '5', '6', '7', '8', '9', 'T', 'J', 'Q', 'K', 'A'];

const BlastAnswerScreen = () => {
  const [range, setRange] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { handleContinue } = useGame();

  const defaultState = {
    heroPosition: 'BTN',
    betType: 'FACING_3BET_4BET',
    villainPosition: '',
    actionTaken: 'fold',
    frequency: 0,
    actualFrequency: 0,
    isCorrect: false
  };

  const state = {
    ...defaultState,
    ...(location.state || {})
  };

  if (!state.selectedHand || !state.selectedHand.card1 || !state.selectedHand.card2) {
    state.selectedHand = { card1: 'Ah', card2: 'Ad' };
  }

  const isBlast       = state.quizType === 'BLAST';
  const isFixedHands  = isBlast && !!state.config?.isFixedHands;

  const subRangeFreqMode = isBlast && (
    // 'allin' is included alongside 'jam' because buildBlastConfig maps
    // primaryAction 'open_jam' to filterMode/subtype 'allin' (PRIMARY_FILTER.open_jam
    // in blastRangeRules.js), not 'jam' — an isolated Open Jam quiz (action:'open')
    // arrives here with subtype 'allin'. Without it, isolated Open Jam quizzes fell
    // through to binary/Mixed-mode rendering entirely: the answer screen showed the
    // hand's real action+freq with no 'Never' framing when the tested
    // Jam frequency was 0%, and the background graphic came from getRangeSvgPath()
    // instead of the fold-specific asset — even though the jam branch further down in
    // getBlastCorrectLabel/getFixedBlindsSvgPath (configSubtype === 'allin') was
    // already written to handle exactly this case correctly, just unreachable.
    (state.config?.action === 'open' && ['raise', 'jam', 'call', 'limp', 'allin'].includes(state.config?.subtype || state.config?.filterMode || '')) ||
    state.config?.action === 'jam' ||
    state.config?.action === 'check' ||
    // 'allin' covers the BB-vs-SB-limp "Jam" option, which blastRangeRules.js maps to
    // action:'raise', subtype:'allin' for legacy reasons (kept as 'raise' rather than 'jam'
    // so it doesn't get misread as a facing-a-raise context elsewhere). Without this, that
    // one scenario fell through to binary mode and dropped its percentage (e.g. "Jam" instead
    // of "Jam 100%"), while every other Jam quiz correctly showed the percentage.
    (state.config?.action === 'raise' && ['raise-small', 'raise-large', 'allin'].includes(state.config?.subtype || '')) ||
    (state.config?.action === 'call' && (state.config?.villainAction || '').toLowerCase() !== 'limp')
  );

  // ── Fixed Hands: circles state ────────────────────────────────────────────
  const [fhCircles,  setFhCircles]  = useState([]);
  const [fhLoading,  setFhLoading]  = useState(false);

  useEffect(() => {
    if (!isFixedHands) return;
    if (!state.config?.availableStacks?.length || !state.selectedHand) return;

    const loadCircles = async () => {
      setFhLoading(true);
      try {
        const { getRangeFilePath } = await import('../../screens/BlastConfigScreen/blastRangeRules');
        const config          = state.config;
        const availableStacks = config.availableStacks; // largest → smallest
        const handKey         = buildHandKey(state.selectedHand);

        const position = config.position ?? config.heroPosition;

        // Reaction quizzes (hero facing villain's response to hero's own prior
        // open_raise/open_limp) only make sense for stacks where the hand is
        // actually in hero's opening range to begin with — see
        // isHandInOpeningRange above.
        const antecedentFilter = ANTECEDENT_FILTER_BY_PRIMARY[config.primaryAction];
        const needsOorCheck = !!(antecedentFilter && config.secondaryAction);

        const results = await Promise.all(
          availableStacks.map(async (stack) => {
            try {
              const cfg  = { ...config, stack, position };
              let path   = getRangeFilePath(cfg);
              const foldOnly = { stack, actions: [{ label: 'Fold', freq: 0 }] };
              if (!path) return foldOnly;
              if (!path.startsWith('/')) path = '/' + path;

              const res = await fetch(path);
              if (!res.ok) return foldOnly;

              const text = await res.text();

              // Only fall back to the antecedent opening-range guess when this
              // reaction CSV has no defined row for the hand at all at this stack.
              // A real row here (e.g. A5s/A5o listed as 100% call in
              // sb_vs_sb_open_bb_ai_15bb.csv) is a genuine solved answer and must
              // win over isHandInOpeningRange's OOR guess — even when the hand is
              // a 100%-limp (never-raise) hand in the opening-range CSV, as A5s is
              // at 15bb. The antecedent check exists only to disambiguate "hand is
              // in the opening range but folds this line" (Fold) from "hand never
              // reaches this line" (OOR) when the reaction CSV is silent — not to
              // override a real, present answer.
              if (needsOorCheck && !handHasAnyRow(text, handKey)) {
                const inRange = await isHandInOpeningRange(config.format, position, stack, handKey, antecedentFilter);
                if (!inRange) {
                  return { stack, actions: [{ label: 'OOR', freq: 0 }] };
                }
              }

              // "Mixed" quizzes (subRangeFreqMode false) pass no filterMode: circles
              // show the hand's true, full action mix from the CSV for this stack.
              // That's what lets a genuine 50/50 mix (e.g. Jam 50% / Limp 50%) render
              // as a split circle instead of being flattened into "100% Jam".
              //
              // Single-action sub-range quizzes (Jam only, Large Raise only, etc.)
              // instead filter down to just the tested action vs. Fold, so these
              // circles agree with the oval graphic/label above them — a hand that's
              // really Jam 50%/Raise 50% must show "Jam 50%/Fold 50%" on a Jam-only
              // quiz, not a Jam/Raise split that implies raise is part of this test.
              const circleFilterMode = subRangeFreqMode ? getSubRangeFilterMode(config) : '';
              // foldableContext: false for BB facing SB's limp — SB only
              // completed to 1bb, there's no bet for BB to fold to, so BB's
              // options here are strictly raise (some size) or check, never
              // fold. Without this, a filtered sub-range circle (e.g. "Large
              // Raise only") mislabeled the hand's real other action as Fold
              // instead of Check/the other raise size — see parseHandActions'
              // foldableContext doc comment for the KQo 25bb repro.
              const foldableContext = config.primaryAction !== 'bb_vs_sb_call'
                && config.primaryAction !== 'bb_vs_sb_call_vs_btn_open';
              // showRealActionWhenTestedIsZero: false (2026-09-06, see
              // feedback_blast_subrange_answer_screen_fold.md) — a sub-range
              // quiz tests ONE action in isolation, so a circle whose tested
              // action never happens must not reveal the hand's real,
              // untested action either (that's the complete-range answer,
              // out of scope for this quiz). false gets the plain filtered
              // tally instead: bare Fold@0% when the tested action is 0%.
              const actions = parseHandActions(text, handKey, position, config.action, circleFilterMode, false, foldableContext);
              // Relabel that bare "tested action never happens" result as
              // neutral gray OOR rather than leaving it as blind Fold — same
              // fh-circle--oor treatment already used for antecedent-OOR
              // circles above, and the same reasoning as the oval's own
              // out-of-range graphic (getFixedBlindsSvgPath). Mixed-mode
              // circles (subRangeFreqMode false, circleFilterMode '') are
              // unaffected — this only fires when a sub-range filter was
              // actually applied.
              //
              // OOR rules (confirmed with user 2026-09-26):
              // 1. Reaction quizzes (hero limped/raised first, now facing
              //    villain's response): the hand is dealt from hero's opening
              //    range, so the decision is purely respond vs fold. The ONLY
              //    OOR allowed is the antecedent one above — a stack where the
              //    hand isn't in hero's opening range at all. Anything else is
              //    Fold. (e.g. T5o on "SB Limp-call vs BB Jam": limps 25–6bb,
              //    never calls the jam → Fold at every stack, not OOR.)
              // 2. Other sub-range quizzes: OOR only when the hand really plays
              //    a different action here (e.g. Raise 100% on a Jam-only
              //    quiz). If its real, unfiltered strategy is also pure Fold,
              //    it's a genuine Fold.
              const testedIsZero = subRangeFreqMode && actions.length === 1 && actions[0].label === 'Fold' && actions[0].freq === 0;
              let finalActions = actions;
              if (testedIsZero && !needsOorCheck) {
                const realActions = parseHandActions(text, handKey, position, config.action, '', false, foldableContext);
                if (realActions.some(a => a.label !== 'Fold' && a.freq > 0)) {
                  finalActions = [{ label: 'OOR', freq: 0 }];
                }
              }
              return { stack, actions: finalActions };
            } catch {
              return { stack, actions: [{ label: 'Fold', freq: 0 }] };
            }
          })
        );

        // OOR only makes sense as a contrast across stacks ("in range at
        // 25bb, out of range at 8–6bb"). If EVERY stack came back OOR the
        // answer would be a single OOR circle, which is never valid — the
        // dealt hand is always in range at the current stack, so that only
        // happens via a data/lookup failure. Fall back to Fold instead.
        const allOor = results.length > 0 && results.every(r => r.actions.length === 1 && r.actions[0].label === 'OOR');
        const safeResults = allOor
          ? results.map(r => ({ ...r, actions: [{ label: 'Fold', freq: 0 }] }))
          : results;

        const currentStackNum = Number(state.config?.stack) || null;
        setFhCircles(buildCircleGroups(safeResults, currentStackNum));
      } catch (err) {
        console.error('Fixed Hands circles error:', err);
      } finally {
        setFhLoading(false);
      }
    };

    loadCircles();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFixedHands]);

  // ── Fixed Blinds: row count for the 'other hands' grid ────────────────────
  // The whole screen scrolls (see .answer-scroll in blast-answer-screen.css,
  // same pattern as BlastConfigScreen/QuizConfigScreen) — the browser handles
  // the actual overflow natively. This row count is only used to decide when
  // to show the "more content below" gradient hint. Was previously 3 rows
  // for the correct screen (vs 2 for incorrect, which has an extra "You
  // Selected" block above the oval pushing the grid down further) — but a
  // correct screen with exactly 3 rows already overflows and scrolls, so the
  // hint has to show at that point too. Both outcomes use the same
  // threshold now.
  const CARDS_PER_ROW = 4;
  const [rangeHandsCount, setRangeHandsCount] = useState(0);
  const rangeRowCount = Math.ceil(rangeHandsCount / CARDS_PER_ROW) || 0;
  const rangeScrollThreshold = 2; // rows beyond this trigger the scroll hint
  const shouldScrollRange = !isFixedHands && rangeRowCount > rangeScrollThreshold;

  // ── Standard range loading (Fixed Blinds / Cash game) ────────────────────
  useEffect(() => {
    if (isFixedHands) {
      setLoading(false); // Fixed Hands never uses the standard range loader
      return;
    }
    const loadRange = async () => {
      try {
        setLoading(true);
        setError(null);

        let filePath;

        if (isBlast) {
          // ── Blast: derive path from blast config ────────────────────────────
          // Dynamic import to avoid circular deps at top level
          const { getRangeFilePath } = await import('../../screens/BlastConfigScreen/blastRangeRules');
          const cfg = state.config || {};
          const normalised = { ...cfg, position: cfg.position ?? cfg.heroPosition };
          filePath = getRangeFilePath(normalised);
          if (!filePath) {
            throw new Error('Could not resolve blast range path');
          }
          if (!filePath.startsWith('/')) filePath = '/' + filePath;
        } else {
          // ── Cash game: original path logic ──────────────────────────────────
          const position = state.heroPosition.toLowerCase();
          if (state.betType === 'FACING_3BET_4BET') {
            const threeBetPos = state.config?.threeBetPosition?.toLowerCase() || 'sb';
            filePath = `/database/preflop/ranges/${position}/${position}_4bet-${threeBetPos}_3bet.csv`;
          } else if (state.betType === 'FACING_OPEN_3BET') {
            const openPos = state.config?.openPosition?.toLowerCase() || 'hj';
            filePath = `/database/preflop/ranges/${position}/${position}_3bet-${openPos}_open.csv`;
          } else if (state.betType === 'OPEN') {
            filePath = `/database/preflop/ranges/${position}/${position}_open.csv`;
          } else {
            filePath = state.config?.range || `/database/preflop/ranges/${position}/${position}_open.csv`;
          }
        }

        const response = await fetch(filePath);
        if (!response.ok) throw new Error(`Failed to load range file (${response.status})`);

        const text = await response.text();
        const lines = text.trim().split('\n');
        const headers = lines[0].toLowerCase().split(',').map(h => h.trim());
        const handIdx = headers.indexOf('hand');
        const typeIdx = headers.indexOf('type');
        const freqIdx = headers.indexOf('frequency');
        const actIdx  = headers.indexOf('action');
        const startIndex = handIdx !== -1 ? 1 : 0;

        const hands = [];
        for (let i = startIndex; i < lines.length; i++) {
          const parts = lines[i].split(',').map(v => v.trim());
          if (parts.length < 2) continue;

          const hand = handIdx !== -1 ? parts[handIdx] : parts[0];
          const type = typeIdx !== -1 ? parts[typeIdx] : (parts[1] || '');
          const freqRaw = freqIdx !== -1 ? parts[freqIdx] : (parts[2] || '0');
          const frequency = parseInt(freqRaw.replace('%', ''));
          const action = actIdx !== -1 ? parts[actIdx].toLowerCase() : '';

          if (!hand || isNaN(frequency) || frequency <= 0) continue;

          const baseHand = hand.replace(/[so]$/, '');
          if (baseHand[0] === baseHand[1]) {
            hands.push({ card1: baseHand[0] + 'h', card2: baseHand[0] + 'd', frequency, action });
          } else if (type === 'suited') {
            hands.push({ card1: baseHand[0] + 'h', card2: baseHand[1] + 'h', frequency, action });
          } else {
            hands.push({ card1: baseHand[0] + 'h', card2: baseHand[1] + 'd', frequency, action });
          }
        }

        // For Blast sub-range quizzes, filter the range to match the quiz's specific
        // action type (jam, raise, call/limp) so the answer screen shows only the
        // relevant hands — not the full open range.
        let filteredHands = hands;
        if (isBlast) {
          const subtype = (state.config?.subtype || state.config?.filterMode || '').toLowerCase();
          if (subtype && subtype !== 'all') {
            // Precompute raise-size info once (used only by the raise-large/raise-small
            // branch below), the same way parseHandActions' Step 1 does: scan every raise
            // row across the whole range to find the distinct raise sizes — largest wins
            // "Large Raise".
            const raiseActionSet = new Set();
            for (const h of hands) {
              const a = (h.action || '').toLowerCase();
              if (a === 'raise' || a.startsWith('raise_')) raiseActionSet.add(a);
            }
            const raiseSizesSorted = [...raiseActionSet]
              .map(a => ({ action: a, size: a.startsWith('raise_') ? (parseFloat(a.slice(6)) || 0) : 0 }))
              .sort((a, b) => b.size - a.size);
            const hasMultipleRaiseSizes = raiseSizesSorted.length >= 2;
            const largestRaiseAction = raiseSizesSorted[0]?.action ?? null;

            filteredHands = hands.filter(h => {
              const act = (h.action || '').toLowerCase();
              // 'allin' is the raw CSV action for Jam. buildBlastConfig maps BOTH an
              // isolated Open Jam quiz and a Jam-response sub-range quiz to
              // subtype/filterMode 'allin', not 'jam' (PRIMARY_FILTER.open_jam /
              // SECONDARY_FILTER.jam in blastRangeRules.js, then `subtype: filterMode ??
              // subtype` overwrites the legacy 'jam' value at config-build time). Without
              // matching 'allin' here, this check never fired and fell through to the
              // catch-all `return true` below — every hand in the range (Call, Raise,
              // everything) was shown as "playing the same" as a Jam-only tested hand,
              // teaching the wrong hands as equivalent to a solved 100% jam.
              if (subtype === 'jam' || subtype === 'allin')       return act === 'allin' || act === 'jam';
              if (subtype === 'raise' || subtype === 'raise-all') return act === 'raise' || act.startsWith('raise_');
              // 'check' = SB limp in solver CSVs (see parseHandActions above for the
              // full rationale) — without it, every SB Limp sub-range's "Other Qx
              // hands" pool comes back empty even though matching hands exist
              // (e.g. sb_open_15bb.csv: Q5s 50% allin / 50% check).
              if (subtype === 'call' || subtype === 'limp')       return act === 'call' || act === 'limp' || act === 'check';
              // A literal Check-response quiz (BB Option Type facing SB limp) — distinct
              // from the SB-limp-as-'check' alias just above; here 'check' IS the tested
              // action, not a stand-in for call/limp.
              if (subtype === 'check')                            return act === 'check';
              if (subtype === 'raise-large' || subtype === 'raise-small') {
                // Only one raise size exists at this stack — same fallback parseHandActions'
                // raiseLabel() uses: no split to distinguish large from small, so any raise
                // row belongs to whichever raise sub-range is being tested.
                if (!hasMultipleRaiseSizes) return act === 'raise' || act.startsWith('raise_');
                return subtype === 'raise-large'
                  ? act === largestRaiseAction
                  : (act.startsWith('raise') && act !== largestRaiseAction);
              }
              return true;
            });
          } else {
            // No explicit subtype ('all' / unset) — this is a full Jam/Raise/Limp/
            // Mixed quiz, so `hands` still holds every CSV row per hand (a mixed hand
            // like K5o has both an 'allin' row and a 'raise' row). The "Other Kx
            // hands" list must match the selected hand's FULL action mix exactly —
            // not just share one action bucket — otherwise e.g. K4o's Raise 50%/
            // Check 50% mix showed up under "Other Kx hands that Jam/Check" just
            // because its Jam-bucket row happened to also read 50%, even though its
            // other 50% is a totally different action (Raise, not Check).
            const heroPos   = state.heroPosition || state.config?.heroPosition || '';
            const isCallCtx = state.config?.action === 'call' || state.config?.action === 'jam';
            const raiseSizesArr = state.raiseSizes || [];

            // Group every CSV row by hand key so a mixed hand's full action list —
            // not just whichever row a single-action filter happens to keep — is
            // visible for the signature comparison below.
            const byHand = new Map();
            for (const h of hands) {
              const key = h.card1[0] === h.card2[0]
                ? h.card1[0] + h.card1[0]
                : (h.card1[1] === h.card2[1] ? `${h.card1[0]}${h.card2[0]}s` : `${h.card1[0]}${h.card2[0]}o`);
              if (!byHand.has(key)) byHand.set(key, []);
              byHand.get(key).push(h);
            }

            const signatureOf = (rows) => buildMergedLabelEntries(
              rows.map(r => ({ action: r.action, frequency: r.frequency })),
              raiseSizesArr,
              heroPos,
              isCallCtx
            );

            // The reference signature is the selected hand's own full action mix.
            // state.allActions already carries every real CSV row for that hand
            // (see useBlastHandGenerator); fall back to the single correctAction/
            // frequency pair on the rare path where it's missing.
            const referenceRows = (state.allActions && state.allActions.length)
              ? state.allActions
              : [{ action: state.correctAction, frequency: state.frequency }];
            const referenceSignature = signatureOf(referenceRows);

            if (referenceSignature.length === 0) {
              // Selected hand is a genuine 100% fold — folds are implicit and
              // never written to the range CSV, so there is no `byHand` group
              // with a matching (empty) signature to find; the loop below would
              // always return zero hands, even when other same-high-card hands
              // also fold. Pass the full unfiltered range through instead:
              // RangeContainer's analyzeRange already treats "absent from
              // range" as "folds", so it can correctly surface e.g. other 7x
              // offsuit hands that fold too, rather than showing nothing.
              filteredHands = hands;
            } else {
              filteredHands = [];
              for (const rows of byHand.values()) {
                if (sameActionSignature(signatureOf(rows), referenceSignature)) {
                  filteredHands.push(rows[0]);
                }
              }
            }
          }
        }

        setRange(filteredHands);
        setError(null);
      } catch (err) {
        console.error('Error loading range:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadRange();
  }, []);

  // ── You Selected label (incorrect answers only) ───────────────────────────
  const getYouSelectedLabel = () => {
    const chosen = state.chosenAction || '';
    if (!chosen) return '';

    if (subRangeFreqMode) {
      // New-style percentage buttons: '100%', '50%', '0%'
      if (chosen === '100%' || chosen === '50%' || chosen === '0%') {
        if (chosen === '0%') return 'Never';
        const pct = parseInt(chosen);
        const position = state.heroPosition || state.config?.heroPosition || '';
        const a = (state.correctAction || '').toLowerCase();
        const configAction = state.config?.action;
        const configSubtype = state.config?.subtype || state.config?.filterMode || '';

        let actionLabel;
        if (configAction === 'check') {
          actionLabel = 'Check';
        } else if (
          configAction === 'call' ||
          (configAction === 'open' && (configSubtype === 'call' || configSubtype === 'limp'))
        ) {
          const isOpenLimp = configAction === 'open';
          actionLabel = position === 'SB' && isOpenLimp ? 'Limp' : 'Call';
        } else if (
          configSubtype === 'raise' ||
          configSubtype?.startsWith('raise_') ||
          configSubtype === 'raise-small' ||
          configSubtype === 'raise-large'
        ) {
          // Raise sub-range: use Large/Small when the subtype specifies it
          actionLabel = configSubtype === 'raise-large' ? 'Large Raise'
                      : configSubtype === 'raise-small' ? 'Small Raise'
                      : 'Raise';
        } else if (
          configAction === 'jam' ||
          configSubtype === 'jam' ||
          configSubtype === 'allin'
        ) {
          // Jam sub-range: label is always "Jam X%"
          actionLabel = 'Jam';
        } else {
          actionLabel = normalizeCircleAction(a, 100, position);
          if (actionLabel === 'Fold') return 'Never';
        }
        return `${actionLabel} ${pct}%`;
      }
      // Legacy labels already have action+freq (e.g. 'Raise 50%') — return as-is
      if (chosen === 'Never') return 'Never';
      return chosen;
    }

    // Binary mode: show the chosen action directly
    return chosen;
  };

  // ── Stack text for the active circle group ─────────────────────────────────
  const getActiveStackText = () => {
    const currentStack = Number(state.config?.stack);
    if (!currentStack) return '';
    return `${currentStack}bb`;
  };

  // ── Range SVG for Mixed Blinds background graphic ─────────────────────────
  // Picks the correct _bg.svg based on what action(s) the hand takes at this stack.
  const getRangeSvgPath = () => {
    const allActions = state.allActions && state.allActions.length > 0
      ? state.allActions
      : [{ action: state.correctAction, frequency: state.frequency ?? 0 }];

    const raiseSizes = state.raiseSizes || []; // sorted largest first
    const hasMultipleRaises = raiseSizes.length >= 2;
    const largestRaiseSize  = raiseSizes[0];

    let hasJam        = false;
    let hasLargeRaise = false;
    let hasSmallRaise = false;
    let hasSingleRaise= false;
    let hasCheckLimp  = false;

    for (const { action, frequency } of allActions) {
      if (!frequency || frequency <= 0) continue;
      const a = (action || '').toLowerCase();
      if (a === 'allin') { hasJam = true; continue; }
      if (a === 'raise' || a.startsWith('raise_')) {
        if (hasMultipleRaises) {
          const size = a.startsWith('raise_') ? parseFloat(a.split('_')[1]) : 0;
          if (size >= largestRaiseSize) hasLargeRaise = true;
          else hasSmallRaise = true;
        } else {
          hasSingleRaise = true;
        }
        continue;
      }
      if (a === 'call' || a === 'limp' || a === 'check') { hasCheckLimp = true; continue; }
    }

    // For single-raise context (no large/small distinction at this stack), use
    // configSubtype to tell large vs generic raise apart — computed up front so
    // both the jam-combo branches and the plain-raise branches below can use it.
    const singleRaiseSubtype = (state.config?.subtype || state.config?.filterMode || '').toLowerCase();
    const singleRaiseIsLarge = singleRaiseSubtype === 'raise-large' || singleRaiseSubtype === 'raise_large';

    if (hasJam && hasCheckLimp)                  return '/svgs/jam_check_bg.svg';
    if (hasJam && hasLargeRaise)                 return '/svgs/jam_large_raise_bg.svg';
    // hasSmallRaise / hasSingleRaise combined with Jam previously fell through to
    // the plain hasJam branch below (jam_bg.svg), silently dropping the raise half
    // of a genuine Jam/Raise mix (e.g. SB Open 15bb K5o: allin 50% / raise 50%) and
    // showing a solid 100%-jam graphic on a hand that's actually a 50/50 split —
    // reported bug where "Jam 50%" text didn't match a 100%-jam-looking background.
    if (hasJam && hasSmallRaise)                 return '/svgs/jam_small_raise_bg.svg';
    if (hasJam && hasSingleRaise) {
      return singleRaiseIsLarge ? '/svgs/jam_large_raise_bg.svg' : '/svgs/jam_small_raise_bg.svg';
    }
    if (hasJam)                                   return '/svgs/jam_bg.svg';
    if (hasLargeRaise && hasSmallRaise)           return '/svgs/large_raise_small_raise_bg.svg';
    if (hasLargeRaise && hasCheckLimp)            return '/svgs/large_raise_check_bg.svg';
    if (hasLargeRaise)                            return '/svgs/large_raise_bg.svg';
    if (hasSmallRaise && hasCheckLimp)            return '/svgs/raise_check_bg.svg';
    if (hasSmallRaise)                            return '/svgs/raise_bg.svg';
    if (hasSingleRaise && hasCheckLimp) {
      return singleRaiseIsLarge ? '/svgs/large_raise_check_bg.svg' : '/svgs/raise_check_bg.svg';
    }
    if (hasSingleRaise) {
      return singleRaiseIsLarge ? '/svgs/large_raise_bg.svg' : '/svgs/raise_bg.svg';
    }
    if (hasCheckLimp)                             return '/svgs/check_call_bg.svg';
    // Hand not in range — fold/never
    return '/svgs/never_bg.svg';
  };

  // ── Sub-range specific-graphic picker ──────────────────────────────────────
  // Used by BOTH single-stack Fixed Blinds questions and Mixed Blinds/Fixed
  // Hands circles — any answer screen testing a single sub-range action in
  // isolation (subRangeFreqMode). The shared ovals above (jam_check_bg,
  // large_raise_check_bg, etc.) each pair two *named* actions — there's no
  // "one action + implied fold" variant in that set. When a hand is a clean
  // 50/50 between exactly one real action and fold (allActions has exactly
  // one entry, at 50%), use the dedicated *_50_specific_bg.svg asset instead;
  // everything else falls back to getRangeSvgPath() unchanged, so Mixed
  // (full-strategy) ovals are never affected.
  const getFixedBlindsSvgPath = () => {
    const allActions = state.allActions;

    // Fixed-line quizzes (subRangeFreqMode — e.g. "should I limp here") only
    // test ONE action type, even though the hand's underlying CSV strategy may
    // be a genuine 50/50 mix with a *different* action (e.g. Jam 50% / Limp
    // 50%). In that case allActions.length is 2, so the check below never
    // fires, and this used to fall through to getRangeSvgPath(), which shows
    // the two-real-action oval (e.g. jam_check_bg.svg) — implying the quiz is
    // testing both Jam and Limp together. It isn't: only Limp is being tested,
    // so the specific single-action asset (check_50_specific_bg.svg) is what
    // should show, same as if the CSV row for the untested action didn't exist
    // at all. Reuse getBlastCorrectLabel(), which already isolates the tested
    // action's own frequency the same way for the text label above.
    if (subRangeFreqMode) {
      const label = getBlastCorrectLabel();
      if (label && label.endsWith(' 50%')) {
        const base = label.slice(0, -4);
        if (base === 'Jam')                            return '/svgs/jam_50_specific_bg.svg';
        if (['Check', 'Call', 'Limp'].includes(base))  return '/svgs/check_50_specific_bg.svg';
        if (base === 'Large Raise')                     return '/svgs/raise_large_50_specific_bg.svg';
        if (['Small Raise', 'Raise'].includes(base))    return '/svgs/raise_small_open_50_specific_bg.svg';
      }
      // The tested action's own frequency is 0% for this hand/stack — the
      // correct call is plain "Fold", regardless of what the hand's real,
      // untested strategy does instead (e.g. a Jam-only quiz on a hand that's
      // really Raise 50%/Fold 50% still shows the Fold-specific graphic, not
      // raise_bg.svg, since raise was never part of this quiz). Pick the
      // fold-specific asset for whichever action family is under test, so the
      // "other 50%/100%" side of the oval reads as neutral gray, not as if
      // fold were a second named action being tested alongside it.
      // A genuine fold (the hand truly folds this decision) stays on the
      // fold-specific blue asset: fold is a real option *within* the decision
      // being tested. Any other real action means the hand isn't a candidate
      // for this decision at all — it belongs to a different node/strategy
      // entirely (e.g. a 15bb SB hand that only ever limps has no "raise or
      // fold" choice to begin with), so that case gets the neutral
      // out-of-range gray asset instead of the fold-blue one, which wrongly
      // implied a fold that never happened. answer_graphic_oor.svg (#494744)
      // was added specifically for this — same gray as ACTION_COLORS.OOR,
      // already used by the Fixed Hands circles for the analogous
      // antecedent-OOR case.
      //
      // getNeverOrFoldLabel() now always returns bare 'Never' (2026-09-06,
      // see feedback_blast_subrange_answer_screen_fold.md) — the label text no
      // longer says what the real action is, on purpose, so it can't be
      // pattern-matched here anymore. Recompute the real action the same way
      // getNeverOrFoldLabel used to, purely to pick the right graphic.
      if (label === 'Fold' || label === 'Never') {
        const isCallCtx = state.config?.action === 'call' || state.config?.action === 'jam';
        const heroPos = state.heroPosition || state.config?.heroPosition || '';
        const mergedEntries = buildMergedLabelEntries(state.allActions, state.raiseSizes, heroPos, isCallCtx);
        const real = formatMergedActionLabel(mergedEntries);
        if (real === 'Fold') {
          const targetFilterMode = getSubRangeFilterMode(state.config);
          if (targetFilterMode === 'allin')                                    return '/svgs/jam_fold_specific_bg.svg';
          if (targetFilterMode === 'call' || targetFilterMode === 'check')     return '/svgs/check_fold_specific_bg.svg';
          if (targetFilterMode === 'raise-large')                              return '/svgs/raise_large_fold_specific_bg.svg';
          if (targetFilterMode === 'raise' || targetFilterMode === 'raise-small') return '/svgs/raise_small_open_fold_specific_bg.svg';
          return '/svgs/fold_bg.svg';
        }
        return '/svgs/answer_graphic_oor.svg';
      }
      // Anything else here (100% actions, or combined labels like
      // "Jam/Large Raise" that genuinely test two real actions together)
      // falls through to the existing logic below unchanged.
    }

    const isCleanFiftyFifty = allActions?.length === 1 && allActions[0].frequency === 50;

    if (isCleanFiftyFifty) {
      const a = (allActions[0].action || '').toLowerCase();
      const raiseSizes = state.raiseSizes || [];
      const hasMultipleRaises = raiseSizes.length >= 2;

      if (a === 'allin') return '/svgs/jam_50_specific_bg.svg';
      if (a === 'call' || a === 'limp' || a === 'check') return '/svgs/check_50_specific_bg.svg';
      if (a === 'raise' || a.startsWith('raise_')) {
        if (hasMultipleRaises) {
          const size = a.startsWith('raise_') ? parseFloat(a.split('_')[1]) : 0;
          return size >= raiseSizes[0]
            ? '/svgs/raise_large_50_specific_bg.svg'
            : '/svgs/raise_small_open_50_specific_bg.svg';
        }
        // Only one raise size exists at this stack — fall back to the quiz's
        // configured subtype to tell large vs small/open apart, same as
        // getRangeSvgPath()'s single-raise fallback above.
        const subtype = (state.config?.subtype || state.config?.filterMode || '').toLowerCase();
        const isLarge = subtype === 'raise-large' || subtype === 'raise_large';
        return isLarge
          ? '/svgs/raise_large_50_specific_bg.svg'
          : '/svgs/raise_small_open_50_specific_bg.svg';
      }
      // NOTE: fold_50_specific_bg.svg is reserved for a future "should I fold"
      // quiz mode. There's no config.action === 'fold' quiz today, so it isn't
      // wired up yet — revisit once that mode/asset set exists.
    }

    return getRangeSvgPath();
  };

  // ── Binary mode: action | stackbb label for inside the oval ───────────────
  // Pass includeStack=false to get just the action+freq part (no "| Xbb")
  const getBinaryOvalLabel = (includeStack = true) => {
    const position   = state.heroPosition || state.config?.heroPosition || '';
    const currentStack = state.config?.stack;
    const raiseSizes = state.raiseSizes || []; // sorted largest first

    // When correct on a mixed-action hand (50/50), show the action the user chose
    // rather than always the primary correctAction.
    let resolvedAction = (state.correctAction || '').toLowerCase();
    let resolvedFreq   = state.frequency ?? 100;

    if (state.isCorrect && state.allActions && state.allActions.length > 1 && state.chosenAction) {
      const chosen = state.chosenAction; // e.g. 'Check', 'Raise', 'Large Raise', 'Jam'
      const matchEntry = state.allActions.find(({ action }) => {
        const act = (action || '').toLowerCase();
        if (chosen === 'Jam')                                                  return act === 'allin';
        if (['Raise', 'Large Raise', 'Small Raise'].includes(chosen))         return act === 'raise' || act.startsWith('raise_');
        if (['Check', 'Limp', 'Call', 'Complete'].includes(chosen))           return act === 'check' || act === 'limp' || act === 'call';
        return false;
      });
      if (matchEntry) {
        resolvedAction = (matchEntry.action || '').toLowerCase();
        resolvedFreq   = matchEntry.frequency;
      }
    }

    // Normalise action → display label
    let actionLabel;
    const isRaiseAct = resolvedAction === 'raise' || resolvedAction.startsWith('raise_');
    if (isRaiseAct) {
      if (raiseSizes.length >= 2) {
        // Check if this hand uses multiple raise sizes (mixed strategy)
        const allRaiseEntries = (state.allActions || []).filter(({ action: a }) => {
          const act = (a || '').toLowerCase();
          return act === 'raise' || act.startsWith('raise_');
        });
        if (allRaiseEntries.length >= 2) {
          // Mixed raise sizes — show combined label, large first per project rule
          actionLabel = 'Large/Small Raise';
          resolvedFreq = Math.min(...allRaiseEntries.map(r => r.frequency));
        } else {
          const size = resolvedAction.startsWith('raise_') ? parseFloat(resolvedAction.split('_')[1]) : 0;
          actionLabel = size >= raiseSizes[0] ? 'Large Raise' : 'Small Raise';
        }
      } else {
        actionLabel = 'Raise';
      }
    } else {
      actionLabel = normalizeCircleAction(resolvedAction, resolvedFreq, position, state.config?.action === 'call');
    }

    // getBinaryOvalLabel is only ever used for Mixed-mode quizzes (subRangeFreqMode
    // false — see the `subRangeFreqMode ? getBlastCorrectLabel() : getBinaryOvalLabel(false)`
    // call site). Mixed mode studies the whole Jam/Raise/Limp/Fold decision at
    // once, not a single action's test frequency, so — per product direction —
    // it should never show a percentage: that's what distinguishes it from a
    // sub-range frequency quiz (Jam/Raise/Call-only), where 100%/50%/Never IS
    // the thing being tested and getBlastCorrectLabel shows it accordingly.
    // Previously this showed "Fold 100%" to avoid "Fold 0%" reading as
    // ambiguous, but that percentage was never real frequency data — a Fold
    // answer here always means "absent from the range CSV entirely" (see
    // parseHandActions/lookupBlastHand), so plain "Fold" is both accurate and
    // consistent with how every other 100%-frequency action already renders
    // without a suffix (see freqSuffix below).
    if (actionLabel === 'Fold') {
      const label = 'Fold';
      if (!includeStack) return label;
      return currentStack ? `${label} | ${currentStack}bb` : label;
    }

    // Combined Jam + Large Raise — when raise-large subtype mixes jam and raise actions
    const configSubtypeCheck = (state.config?.subtype || '').toLowerCase();
    if ((configSubtypeCheck === 'raise-large' || configSubtypeCheck === 'raise_large') && state.allActions?.length > 1) {
      const hasJamPart   = state.allActions.some(({ action: a }) => (a || '').toLowerCase() === 'allin');
      const hasRaisePart = state.allActions.some(({ action: a }) => { const act = (a || '').toLowerCase(); return act === 'raise' || act.startsWith('raise_'); });
      if (hasJamPart && hasRaisePart) {
        const freqStr = resolvedFreq < 100 ? ` ${resolvedFreq}%` : '';
        if (!includeStack) return `Jam/Large Raise${freqStr}`;
        return currentStack ? `Jam/Large Raise${freqStr} | ${currentStack}bb` : `Jam/Large Raise${freqStr}`;
      }
    }

    const freqSuffix = resolvedFreq < 100 ? ` ${resolvedFreq}%` : '';
    if (!includeStack) return `${actionLabel}${freqSuffix}`;
    return currentStack ? `${actionLabel}${freqSuffix} | ${currentStack}bb` : `${actionLabel}${freqSuffix}`;
  };

  // For Blast: build a human-readable label for the correct action
  const getBlastCorrectLabel = () => {
    const a = (state.correctAction || '').toLowerCase();
    const f = state.frequency ?? 0;
    const isRaiseAct = (act) => act === 'raise' || (typeof act === 'string' && act.startsWith('raise_'));
    const isLimpAct  = (act) => act === 'call' || act === 'limp' || act === 'check';
    const isRaise    = isRaiseAct(a);
    const isCallAct  = isLimpAct(a);
    const heroPos    = state.heroPosition || state.config?.heroPosition || '';

    // For mixed-strategy hands (multiple CSV rows), compute per-action-type frequencies.
    // e.g. AJo at 25bb SB: raise 50% + check(=limp) 50%
    const allActions = state.allActions;
    const getFreqFor = (predicate) => {
      if (allActions && allActions.length > 1) {
        return allActions.filter(e => predicate(e.action)).reduce((sum, e) => sum + e.frequency, 0);
      }
      return predicate(a) ? f : 0;
    };
    const raiseFreq = getFreqFor(isRaiseAct);
    const limpFreq  = getFreqFor(isLimpAct); // includes 'check' = SB limp in CSVs
    const jamFreq   = getFreqFor(act => act === 'allin');

    // Actual best action for this hand, regardless of which action type the quiz
    // is testing. "Never" only tells you what NOT to do — it doesn't move learning
    // forward. Whenever the queried action's frequency is 0, fall back to this so
    // the incorrect-answer screen always shows the real correct play (Fold, Small
    // Raise, Large Raise, Call, etc.), sometimes with a mixed-frequency suffix.
    const getActualAnswerLabel = () => {
      // CSV data uses 'check' interchangeably with 'call' for facing-a-bet
      // scenarios (see normalizeCircleAction above for the full rationale) — no
      // CSV row ever encodes an explicit 'fold' action, so 'check' never stands
      // in for one. Keep the raw action as-is; isCallCtx below only decides the
      // *label* ('Check' vs 'Call'), not whether the hand folded.
      const isCallCtx = state.config?.action === 'call' || state.config?.action === 'jam';
      const effA = a;

      if (!effA || effA === 'fold' || f === 0) return 'Fold';
      if (effA === 'allin') return f < 100 ? `Jam ${f}%` : 'Jam';
      if (isRaiseAct(effA)) {
        const raiseSizesArr = state.raiseSizes || [];
        if (raiseSizesArr.length >= 2 && effA.startsWith('raise_')) {
          const size = parseFloat(effA.split('_')[1]);
          const label = size >= raiseSizesArr[0] ? 'Large Raise' : 'Small Raise';
          return f < 100 ? `${label} ${f}%` : label;
        }
        return f < 100 ? `Raise ${f}%` : 'Raise';
      }
      if (isLimpAct(effA)) {
        // Same isSB/isCallCtx precedence as normalizeCircleAction above: SB's own
        // opening decision is 'Limp', but SB genuinely calling a bet (isCallCtx)
        // must read as 'Call', not 'Limp' again — checking heroPos==='SB' before
        // isCallCtx here would silently override that, as it did before this fix.
        const callLabel = heroPos === 'SB' ? (isCallCtx ? 'Call' : 'Limp') : (heroPos === 'BB' && !isCallCtx) ? 'Check' : 'Call';
        return f < 100 ? `${callLabel} ${f}%` : callLabel;
      }
      return effA.charAt(0).toUpperCase() + effA.slice(1);
    };

    // ── Never label for isolated 0%-tested-frequency answers ─────────────────
    // Superseded 2026-09-06 (see feedback_blast_subrange_answer_screen_fold.md):
    // sub-range quizzes (Jam only, Raise only, Check/Call/Limp only) test ONE
    // action in isolation. Naming the hand's real action here — even "Fold" —
    // answers a question this quiz isn't asking: it leaks what the hand does
    // on the *complete* range. So the tested-action-is-0% answer is always
    // just "Never", regardless of what the hand's real action actually is.
    // (Previously returned "Never | <real action>" — that 2026-07-12 decision
    // is preserved inline in that file for context.)
    const getNeverOrFoldLabel = () => 'Never';

    // Sub-range frequency mode: show which button was correct
    if (subRangeFreqMode) {
      const configAction = state.config?.action;
      const configSubtype = state.config?.subtype || state.config?.filterMode || '';
      if (configAction === 'check') {
        // This quiz only tests Check/Call/Limp frequency in isolation — when that
        // frequency is 0 for this hand/stack, the answer is always plain "Never" —
        // it never says what the hand actually does instead (e.g. it might Raise
        // 100% elsewhere — this quiz just isn't testing that).
        if (limpFreq === 0) return getNeverOrFoldLabel();
        return limpFreq < 100 ? 'Check 50%' : 'Check 100%';
      }
      if (configAction === 'call' || (configAction === 'open' && (configSubtype === 'call' || configSubtype === 'limp'))) {
        // 'Limp' only for SB's primary open-limp (action='open'); all other call
        // scenarios (SB calling vs jam/raise) use 'Call'.
        const isOpenLimp = configAction === 'open';
        const limpLabel = (heroPos === 'SB' && isOpenLimp) ? 'Limp' : 'Call';
        // Isolated Call/Limp sub-range quiz — 0% here always shows plain "Never",
        // regardless of what the hand's real, untested strategy plays instead.
        if (limpFreq === 0) return getNeverOrFoldLabel();
        return limpFreq < 100 ? `${limpLabel} 50%` : `${limpLabel} 100%`;
      }
      // NOTE: no "combined Jam + Large Raise" special case here (unlike
      // getBinaryOvalLabel's Mixed-mode branch below, which legitimately
      // combines them). This branch only runs when subRangeFreqMode is true —
      // i.e. the quiz is isolated to ONE action (here, Large Raise only). A
      // hand that's really Jam 50%/Large Raise 50% at this stack is still
      // being tested on Large Raise alone: the correct label is "Large Raise
      // 50%", never "Jam/Large Raise 50%" — showing Jam here implied the quiz
      // was testing Jam too, when it wasn't, and paired a two-tone
      // jam_large_raise_bg.svg oval with a quiz that only asked about one of
      // the two actions. The Jam half of the mix still surfaces correctly
      // below, in the per-stack circle's real alternate action (see
      // foldableContext in the Fixed Hands loader above), matching how every
      // other isolated sub-range quiz already reports just its own tested
      // frequency and lets the circle show what the "other" frequency is.
      // Jam context — route strictly by the quiz's configured action/subtype
      // (same source of truth isBlastAnswerCorrect in BlastTable.jsx uses),
      // never by which action happens to be this specific hand's dominant CSV
      // row. A hand can be a genuine mixed Jam/Raise strategy (e.g. 50% allin /
      // 50% raise) while the quiz itself is only testing Raise frequency —
      // checking `a === 'allin'` here matched on the hand's data, not the quiz
      // mode, and forced "Jam 50%" onto a correct Raise-frequency answer.
      if (configAction === 'jam' || configSubtype === 'jam' || configSubtype === 'allin') {
        // Isolated Jam sub-range quiz — 0% jam frequency always shows plain "Never",
        // regardless of what the hand's real, untested strategy plays instead.
        if (jamFreq === 0) return getNeverOrFoldLabel();
        return jamFreq < 100 ? 'Jam 50%' : 'Jam 100%';
      }
      // Raise context — use raiseFreq so mixed raise+limp hands display correctly.
      // Use raiseSizes to label Large vs Small Raise when multiple sizes exist.
      const raiseSizesArr = state.raiseSizes || [];
      const isolatedRaiseSubtype = (configSubtype === 'raise-large' || configSubtype === 'raise_large')
        ? 'large'
        : (configSubtype === 'raise-small' || configSubtype === 'raise_small')
          ? 'small'
          : null;

      let raiseLabel;
      let targetRaiseFreq;

      if (isolatedRaiseSubtype && raiseSizesArr.length >= 2) {
        // Isolated raise-large/raise-small sub-range quiz on a range with a genuine
        // large/small distinction. Only a raise entry whose SIZE matches the size
        // being tested counts — a hand whose real strategy raises only at the OTHER
        // size (e.g. an isolated Small Raise quiz on a hand that only ever raises
        // big here) must read as 0% for this quiz, not silently borrow that other
        // entry's action/frequency. Bug: previously this grabbed whichever raise
        // entry `find()` hit first, and only re-checked size when a hand had 2+
        // raise entries — a hand with a SINGLE raise entry at the wrong size fell
        // through to raw `raiseFreq` (the sum of all raise-type frequencies, which
        // for a single entry equals that one entry) and got labeled/shown as the
        // tested size regardless. That's why the isolated Small Raise answer screen
        // would intermittently flip to "Large Raise" + the large-raise graphic —
        // only on hands whose CSV row for this stack has no small-raise data at all.
        const targetSize = isolatedRaiseSubtype === 'large' ? raiseSizesArr[0] : raiseSizesArr[raiseSizesArr.length - 1];
        const matchingEntry = (allActions || []).find(e => {
          if (!isRaiseAct(e.action) || !e.action.startsWith('raise_')) return false;
          return parseFloat(e.action.split('_')[1]) === targetSize;
        });
        raiseLabel = isolatedRaiseSubtype === 'large' ? 'Large Raise' : 'Small Raise';
        targetRaiseFreq = matchingEntry ? matchingEntry.frequency : 0;
      } else {
        const raiseEntry = (allActions || []).find(e => isRaiseAct(e.action));
        const raiseAct = raiseEntry ? raiseEntry.action : (isRaise ? a : 'raise');
        if (raiseSizesArr.length >= 2 && raiseAct.startsWith('raise_')) {
          const size = parseFloat(raiseAct.split('_')[1]);
          raiseLabel = size >= raiseSizesArr[0] ? 'Large Raise' : 'Small Raise';
        } else if (isolatedRaiseSubtype === 'large') {
          // Fallback: configSubtype tells us this is a large-raise quiz even when raiseSizes < 2
          raiseLabel = 'Large Raise';
        } else if (isolatedRaiseSubtype === 'small') {
          raiseLabel = 'Small Raise';
        } else {
          raiseLabel = normalizeCircleAction(raiseAct, 100, heroPos);
        }
        targetRaiseFreq = raiseFreq;
      }

      // In the isolated-subtype branch, targetRaiseFreq of 0 is an authoritative
      // "this hand doesn't raise at the tested size" — don't let the generic
      // `isRaise` (is the hand's *dominant* action some raise, at any size) bypass
      // that and show a nonzero/mislabeled result.
      const canBypassWithIsRaise = !(isolatedRaiseSubtype && raiseSizesArr.length >= 2);
      if (targetRaiseFreq > 0 || (canBypassWithIsRaise && isRaise)) return targetRaiseFreq < 100 ? `${raiseLabel} 50%` : `${raiseLabel} 100%`;
      // Isolated Raise (large/small) sub-range quiz — 0% here always shows plain
      // "Never", regardless of what the hand's real strategy plays instead.
      return getNeverOrFoldLabel();
    }

    // Standard binary mode. A genuine merged/mixed-range hand (two or more real
    // actions, e.g. Large Raise 50% / Check 50%) shows both actions combined,
    // highest-priority first, with no percentage — the oval's two-color split
    // background already conveys the 50/50 split, so a lone percentage next to
    // one of the two actions was misleading (it read as "Large Raise happens
    // 50% of the time" rather than "the other 50% is Check", per project
    // convention). A hand with only ONE real action (paired with an implicit,
    // unnamed Fold remainder) is unaffected — getActualAnswerLabel() below
    // still shows its own percentage as before.
    const isCallCtx = state.config?.action === 'call' || state.config?.action === 'jam';
    const mergedEntries = buildMergedLabelEntries(allActions, state.raiseSizes, heroPos, isCallCtx);
    if (mergedEntries.length > 1) return formatMergedActionLabel(mergedEntries);
    return getActualAnswerLabel();
  };

  if (loading) {
    return <div className="answer-screen">Loading range data...</div>;
  }

  if (error) {
    return (
      <div className="answer-screen">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  // Cash-game display
  const displayFrequency = state.actualFrequency !== undefined ? state.actualFrequency : state.frequency;
  let actionTypeDisplay = '';
  if (!isBlast) {
    if (state.betType === 'FACING_3BET_4BET') actionTypeDisplay = '4BET';
    else if (state.betType === 'OPEN') actionTypeDisplay = 'OPEN';
    else if (state.betType === 'FACING_OPEN_CALL') actionTypeDisplay = 'CALL';
    else if (state.betType === 'FACING_OPEN_3BET') actionTypeDisplay = '3BET';
    else if (state.betType === 'FACING_3BET_CALL') actionTypeDisplay = 'CALL';
    else if (state.betType === 'FACING_4BET_CALL') actionTypeDisplay = 'CALL';
    else if (state.betType === 'SQUEEZE_4BET') actionTypeDisplay = '4BET';
    else if (state.betType === 'CALL_VS_3BET') actionTypeDisplay = 'CALL';
    else actionTypeDisplay = (state.betType || '').toUpperCase();
  }

  const currentStack = Number(state.config?.stack);
  const isActiveGroup = (group) =>
    currentStack > 0 && group.stacks.map(Number).includes(currentStack);

  const scenarioNote = isFixedHands ? getScenarioNote(state.config) : null;

  // ── Fixed Blinds freq label — always shows percentage ─────────────────────
  const getFixedBlindsLabel = () => {
    const label = getBlastCorrectLabel();
    // Bare 'Never' (see getNeverOrFoldLabel, 2026-09-06) must pass through as-is —
    // it's already the full, final label for a sub-range 0%-tested-freq answer,
    // so it must not get a stray tested-action percent (state.frequency, which
    // is 0 here) appended on top.
    if (!label || label === 'Fold' || label === 'Never') return label;
    if (label.includes('%')) return label;                 // already has it
    if (label.includes('/')) return label;                 // merged-range combo label — no percent by design
    const f = state.frequency ?? 0;
    return `${label} ${f}%`;
  };

  // ── Fixed Blinds caption: format + generated path, same source as Mixed
  // Blinds' scenario label (getScenarioLines), just without the "| Complete
  // Range" suffix — that's Mixed Blinds-only copy.
  const { formatLine, actionLine } = getScenarioLines(state.config, isFixedHands);

  // ── Circle render helper (shared for Fixed Hands circles) ────────────────
  const renderCircles = () => {
    if (fhLoading) return <p className="fh-circles-loading">Loading…</p>;
    return fhCircles.map((group, idx) => {
      const stackText = group.stacks.length > 1
        ? `${group.stacks[0]}–${group.stacks[group.stacks.length - 1]}bb`
        : `${group.stacks[0]}bb`;
      const isSplit  = group.actions.length >= 2;
      const rawLabel1 = group.actions[0]?.label || '';
      const rawLabel2 = group.actions[1]?.label;
      // Circle titles never show the percentage — that's only shown in the oval
      // graphic above (e.g. "Incorrect / Large Raise 50% / 10bb"). The circle's
      // color split (and the oval graphic) already conveys the mix, so we just
      // show the action label here.
      const withFreq = (action) => {
        if (!action) return '';
        return action.label;
      };
      const label1 = withFreq(group.actions[0]);
      const label2 = group.actions[1] ? withFreq(group.actions[1]) : undefined;
      const getSplitLabel = (a, b) => {
        const wordsA = a.split(' ');
        const wordsB = b.split(' ');
        const lastA = wordsA[wordsA.length - 1];
        const lastB = wordsB[wordsB.length - 1];
        if (lastA === lastB && wordsA.length > 1 && wordsB.length > 1) {
          return `${wordsA.slice(0, -1).join(' ')}/\n${wordsB.slice(0, -1).join(' ')} ${lastA}`;
        }
        return `${a}/${b}`;
      };
      const displayLabel = isSplit ? getSplitLabel(label1, label2) : label1;
      const active = isActiveGroup(group);
      // Splits are always an even 50/50 fill — per the range CSV generation rules,
      // every cell is either 100% one action or an exact 50/50 between two actions
      // (no 70/30, 33%, etc. ever occurs by design). Hardcoding 50% here (rather
      // than reading group.actions[0].freq) means the circle can't visually drift
      // from that contract even if a malformed CSV ever produced an odd value.
      const circleStyle = isSplit
        ? {
            backgroundImage: `linear-gradient(to right, ${getActionColor(rawLabel1)} 50%, ${getActionColor(rawLabel2)} 50%)`,
            backgroundOrigin: 'border-box',
            backgroundRepeat: 'no-repeat',
            backgroundSize: '100% 100%',
          }
        : {};
      return (
        <div
          key={idx}
          className={`fh-circle ${isSplit ? '' : circleClass(rawLabel1)} ${active ? 'fh-circle--active' : ''}`}
          style={circleStyle}
        >
          <span className="fh-circle-label">{displayLabel}</span>
          <span className="fh-circle-stacks">{stackText}</span>
        </div>
      );
    });
  };

  return (
    <div className="answer-screen">
      <div className="answer-container">

        <div className="answer-scroll-wrapper">
          <div className="answer-scroll">

            {isFixedHands ? (
              /* ── Mixed Blinds / Fixed Hands (circles) — new oval layout ─── */
              <>
                {!state.isCorrect && (
                  <div className="you-selected-section">
                    <p className="you-selected-label">You Selected</p>
                    <p className="you-selected-value">{getYouSelectedLabel()}</p>
                  </div>
                )}

                <div className={`result-background${state.isCorrect ? ' fh-result-correct-offset' : ' fh-result-incorrect-offset'}`}>
                  <img
                    src={getFixedBlindsSvgPath()}
                    alt=""
                    className="result-svg"
                  />
                  <div className="cards-and-text">
                    <AnswerCards selectedHand={state.selectedHand} />
                    <div className="oval-result-text">
                      <p className="oval-correct-label">
                        {state.isCorrect ? 'Correct' : 'Incorrect'}
                      </p>
                      <div className="oval-answer-detail">
                        <p className="oval-action-freq">
                          {subRangeFreqMode ? getBlastCorrectLabel() : getBinaryOvalLabel(false)}
                        </p>
                        <p className="oval-stack-text">{getActiveStackText()}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="fh-scenario-label">
                  <p className="fh-scenario-format">{formatLine}</p>
                  <p className="fh-scenario-action">{actionLine}</p>
                </div>

                <div className="fh-circles-container">
                  {renderCircles()}
                </div>

              </>
            ) : (
              /* ── Fixed Blinds — oval layout (reuses Mixed Blinds' top section) ── */
              <>
                {!state.isCorrect && (
                  <div className="you-selected-section">
                    <p className="you-selected-label">You Selected</p>
                    <p className="you-selected-value">{getYouSelectedLabel()}</p>
                  </div>
                )}

                <div className={`result-background${state.isCorrect ? ' fh-result-correct-offset' : ' fh-result-incorrect-offset'}`}>
                  <img
                    src={getFixedBlindsSvgPath()}
                    alt=""
                    className="result-svg"
                  />
                  <div className="cards-and-text">
                    <AnswerCards selectedHand={state.selectedHand} />
                    <div className="oval-result-text">
                      <p className="oval-correct-label">
                        {state.isCorrect ? 'Correct' : 'Incorrect'}
                      </p>
                      <div className="oval-answer-detail">
                        <p className="oval-action-freq">{getFixedBlindsLabel()}</p>
                        <p className="oval-stack-text">{getActiveStackText()}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="blast-answer-scenario-caption">
                  <p className="blast-answer-scenario-format">{formatLine}</p>
                  <p className="blast-answer-scenario-path">{actionLine}</p>
                  <p className="blast-answer-same-hands-title">Hands That Play the Same</p>
                </div>

                <RangeContainer
                  selectedHand={state.selectedHand}
                  range={range}
                  cardValues={CARD_VALUES}
                  onHandsResolved={setRangeHandsCount}
                />
              </>
            )}

            <div className="answer-scroll-bottom-spacer" />
          </div>

          {shouldScrollRange && <div className="answer-scroll-gradient" aria-hidden="true" />}
        </div>
      </div>

      {/* Sibling of .answer-container, not nested inside it — .answer-container
          is a narrower (327px) box with its own overflow: hidden, which
          silently clips any position:absolute child that tries to bleed past
          its edges (that's why a negative left/right offset never actually
          worked here). Anchored to .answer-screen instead — a full 375px box,
          same width .blast-screen-content uses on Config/Range/ViewRange — so
          plain horizontalPadding={16} with no offset matches those screens
          exactly, same as everywhere else. */}
      {/* Scenario note lives in the footer (topContent) so it's always
          exactly 8px (rowGap) above the button, regardless of how tall the
          scroll content is — in normal scroll flow its gap to the button
          varied with content height and kept crowding it (2026-09-26). */}
      <QuizFooter
        horizontalPadding={16}
        rowGap={8}
        topContent={scenarioNote ? <p className="fh-answer-note">{scenarioNote}</p> : null}
      >
        <QuizFooterRow gap={10}>
          {!isFixedHands && (
            <ButtonLarge
              text="View Range"
              buttonLargeClassName="button-large view-range-button"
              onClick={() => navigate('/blast-view-range', {
                state: {
                  ...state,
                  config: {
                    ...state.config,
                    heroPosition: state.heroPosition,
                    betType: state.betType
                  },
                  returnTo: '/blast-answer'
                }
              })}
            />
          )}
          <ButtonLarge
            text="Continue"
            buttonLargeClassName="button-large continue-button"
            onClick={() => handleContinue(state)}
          />
        </QuizFooterRow>
      </QuizFooter>
    </div>
  );
};

export default BlastAnswerScreen;
